import { type ParseKeys, type TFunction } from 'i18next'
import { type NotificationType } from '#app/utils/notifications.ts'

/**
 * The `common.notifications.type.*` dictionary key for each stored notification
 * `type` (ADR-078: the row stores the key, never rendered text). Keyed by the
 * `NOTIFICATION_TYPES` registry (`app/utils/notifications.ts`), so a registered
 * type without a label entry is a compile error — while an unknown/historical
 * type still degrades to its raw stored token rather than throwing (rows
 * outlive the code's vocabulary). Adding a new emitter's type is one line in
 * the registry, one here, plus the copy in `common`. Shared by the bell
 * dropdown and the live-arrival toast so a notification reads identically in
 * both.
 */
export const NOTIFICATION_TYPE_KEYS: Record<
	NotificationType,
	ParseKeys<'common'>
> = {
	'account.deactivated': 'notifications.type.account.deactivated',
	'account.reactivated': 'notifications.type.account.reactivated',
	'account.password.reset': 'notifications.type.account.password.reset',
	'account.role.granted': 'notifications.type.account.role.granted',
	'account.role.revoked': 'notifications.type.account.role.revoked',
	'ops.contact-message-received':
		'notifications.type.ops.contact-message-received',
	'ops.integration-failure': 'notifications.type.ops.integration-failure',
}

/**
 * Render a notification's title from its stored `type` key + `params` blob. An
 * unmapped type falls back to the raw token so an unknown/historical event still
 * shows something rather than throwing.
 */
export function notificationTitle(
	t: TFunction,
	type: string,
	params: Record<string, unknown> | null,
): string {
	// Widen the typed map for the lookup: `type` comes from a DB row and may
	// predate the current registry.
	const typeKey = (
		NOTIFICATION_TYPE_KEYS as Partial<Record<string, ParseKeys<'common'>>>
	)[type]
	return typeKey ? t(typeKey, (params ?? {}) as Record<string, unknown>) : type
}
